import React, { useState } from "react";
import { Column } from "@tanstack/react-table";
import {
  ChevronUp,
  ChevronDown,
  Search,
  ArrowUp,
  ArrowDown,
  EyeOff,
  X,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "./utils";

// Type definitions for filter values
export interface SearchFilter {
  type: "search";
  value: string;
}

export interface ExactFilter {
  type: "exact";
  values: any[];
}

export type FilterValue =
  | SearchFilter
  | ExactFilter
  | any[]
  | string
  | undefined;

interface ColumnHeaderProps {
  column: Column<any, unknown>;
  title: string;
  table: any; // Add table prop
}

/**
 * A component for rendering a column header with sorting and multi-select filtering capabilities
 */
export const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  column,
  title,
  table,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(() => {
    const filterValue = column.getFilterValue() as FilterValue;
    if (
      filterValue &&
      typeof filterValue === "object" &&
      !Array.isArray(filterValue) &&
      (filterValue as SearchFilter).type === "search"
    ) {
      return (filterValue as SearchFilter).value || "";
    }
    return "";
  });

  // Get current filter values as an array (for multi-select)
  const currentFilterValues = (() => {
    const filterValue = column.getFilterValue() as FilterValue;

    // Handle structured filter objects
    if (
      filterValue &&
      typeof filterValue === "object" &&
      !Array.isArray(filterValue)
    ) {
      if ((filterValue as ExactFilter).type === "exact") {
        return (filterValue as ExactFilter).values || [];
      }
      if ((filterValue as SearchFilter).type === "search") {
        return []; // Search doesn't show as selected values
      }
    }

    // Legacy support for direct arrays
    if (Array.isArray(filterValue)) {
      return filterValue;
    }

    // Legacy support for direct strings (but not for search)
    return filterValue && typeof filterValue === "string" ? [filterValue] : [];
  })();

  // Format value for display, handling Date objects specially
  const formatValue = (val: any): string => {
    if (val instanceof Date) {
      return formatDate(val);
    }

    if (typeof val === "object" && val !== null) {
      // Handle any other object type by converting to string representation
      return String(val);
    }

    return String(val);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    // When searching, use a special format to indicate this is a search
    if (value.trim()) {
      column.setFilterValue({ type: "search", value: value });
    } else {
      column.setFilterValue(undefined);
    }
  };

  const handleSortAscending = () => {
    // Get current sorting state and metadata from the table
    const currentSorting = table.getState().sorting || [];
    const tableMeta = table.options.meta as any;
    const dateSortActivated = tableMeta?.dateSortActivated || false;

    // Check if we're in the default state (date descending only AND not intentionally activated)
    const isDefaultSort =
      currentSorting.length === 1 &&
      currentSorting[0].id === "date" &&
      currentSorting[0].desc === true &&
      !dateSortActivated;

    // If in default state, replace with single sort; otherwise use multi-sort
    const useMultiSort = !isDefaultSort;

    column.toggleSorting(false, useMultiSort);
    setIsOpen(false);
  };

  const handleSortDescending = () => {
    // Get current sorting state and metadata from the table
    const currentSorting = table.getState().sorting || [];
    const tableMeta = table.options.meta as any;
    const dateSortActivated = tableMeta?.dateSortActivated || false;
    const setDateSortActivated = tableMeta?.setDateSortActivated;

    // Check if we're in the default state (date descending only AND not intentionally activated)
    const isDefaultSort =
      currentSorting.length === 1 &&
      currentSorting[0].id === "date" &&
      currentSorting[0].desc === true &&
      !dateSortActivated;

    // Special case: if clicking date descending while in default state
    // This "activates" the date sort for multi-sort mode (user can then add more sorts)
    if (isDefaultSort && column.id === "date") {
      // Mark the date sort as intentionally activated
      if (setDateSortActivated) {
        setDateSortActivated(true);
      }
      // Don't toggle - just keep it descending and ready for multi-sort
      setIsOpen(false);
      return;
    }

    // If in default state, replace with single sort; otherwise use multi-sort
    const useMultiSort = !isDefaultSort;

    column.toggleSorting(true, useMultiSort);
    setIsOpen(false);
  };

  const handleHideColumn = () => {
    column.toggleVisibility(false);
    setIsOpen(false);
  };

  const handleFilterSelect = (value: any) => {
    // When using multi-select, clear search input first
    setSearchValue("");

    const currentValues = currentFilterValues;
    const valueIndex = currentValues.findIndex((v) => v === value);

    let newValues: any[];
    if (valueIndex >= 0) {
      // Remove the value if it's already selected
      newValues = currentValues.filter((_, index) => index !== valueIndex);
    } else {
      // Add the value if it's not selected
      newValues = [...currentValues, value];
    }

    // Set filter value with exact match indicators
    if (newValues.length === 0) {
      column.setFilterValue(undefined);
    } else {
      // Always use array format for exact matches to distinguish from search
      column.setFilterValue({ type: "exact", values: newValues });
    }
  };

  const clearAllFilters = () => {
    column.setFilterValue(undefined);
    setSearchValue("");
  };

  // Check if we're in search mode or filter mode
  const isInSearchMode = searchValue !== "";
  const isFiltered = column.getIsFiltered();
  const sortedIndex = column.getSortIndex();
  const isSorted = column.getIsSorted();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-1 cursor-pointer w-full">
          <button
            type="button"
            className="flex items-center gap-1 group text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300"
          >
            {title}
            <span className="flex flex-col ml-1">
              <ChevronUp
                className={`h-3 w-3 ${
                  column.getIsSorted() === "asc"
                    ? "text-green-600"
                    : "text-gray-300"
                }`}
              />
              <ChevronDown
                className={`h-3 w-3 -mt-1 ${
                  column.getIsSorted() === "desc"
                    ? "text-green-600"
                    : "text-gray-300"
                }`}
              />
            </span>
          </button>
          {(isFiltered || column.getIsSorted()) && (
            <div className="w-2 h-2 bg-green-600 rounded-full ml-1"></div>
          )}
          {isFiltered && currentFilterValues.length > 1 && (
            <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-1 rounded">
              {currentFilterValues.length}
            </span>
          )}
          {isSorted && sortedIndex > 0 && (
            <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-semibold">
              {sortedIndex + 1}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <div className="p-4 space-y-3">
          {/* Search Bar */}
          {column.getCanFilter() && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
              <Input
                placeholder="Search..."
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchValue && (
                <button
                  onClick={() => handleSearchChange("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  type="button"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-1">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-10 text-sm font-normal"
              onClick={handleSortAscending}
            >
              <ArrowUp className="h-4 w-4" />
              Sort ascending
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-10 text-sm font-normal"
              onClick={handleSortDescending}
            >
              <ArrowDown className="h-4 w-4" />
              Sort descending
            </Button>

            {column.getCanHide() && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-10 text-sm font-normal"
                onClick={handleHideColumn}
              >
                <EyeOff className="h-4 w-4" />
                Hide column
              </Button>
            )}
          </div>

          {/* Filter Values */}
          {column.getFacetedUniqueValues() && (
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Filter by value:
                </div>
                {currentFilterValues.length > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {Array.from(column.getFacetedUniqueValues().keys())
                  .sort((a, b) => String(a).localeCompare(String(b)))
                  .filter((val) => {
                    // Filter options based on search value
                    if (!searchValue) return true;
                    const displayValue = formatValue(val).toLowerCase();
                    return displayValue.includes(searchValue.toLowerCase());
                  })
                  .map((val) => {
                    const count = column.getFacetedUniqueValues().get(val) || 0;
                    const displayValue = formatValue(val);
                    const isSelected = currentFilterValues.includes(val);

                    return (
                      <Button
                        key={String(val)}
                        variant="ghost"
                        className={`w-full justify-between text-xs h-8 font-normal ${
                          isSelected
                            ? "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50"
                            : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        }`}
                        onClick={() => handleFilterSelect(val)}
                      >
                        <span className="truncate flex items-center">
                          {isSelected && (
                            <span className="text-green-600 dark:text-green-400 mr-1 text-sm">
                              ✓
                            </span>
                          )}
                          {displayValue}
                        </span>
                        <span className="ml-2 text-gray-400 dark:text-gray-500 flex-shrink-0">
                          {count}
                        </span>
                      </Button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
